/**
 * analytics.js — send.intake.dental
 *
 * Lightweight GA4 wrapper for the Secure File Transfer app.
 * Uses vanilla gtag.js (no npm dependency) — injects the script tag
 * dynamically on first initGA() call.
 *
 * Measurement ID resolution:
 *   - import.meta.env.VITE_GA_ID — platform GA4 property
 *   - If unset, initGA is a silent no-op (no placeholder IDs fired).
 *
 * send.intake.dental is platform-owned and single-tenant, so there's
 * no per-practice override to worry about (unlike the patient-portal).
 *
 * Usage:
 *   import { initGA, logPageView } from './utils/analytics';
 *   useEffect(() => { initGA(); }, []);
 *   useEffect(() => { logPageView(location.pathname); }, [location]);
 */

const MEASUREMENT_ID = import.meta.env.VITE_GA_ID || null;

let _initialized = false;

function isRealGaId(s) {
  if (!s || typeof s !== 'string') return false;
  if (!/^G-[A-Z0-9]+$/i.test(s)) return false;
  if (s === 'G-XXXXXXXXXX' || s.includes('PLACEHOLDER')) return false;
  return true;
}

// Query params worth keeping for campaign reporting — everything else is
// dropped before a URL reaches GA.
const SAFE_QUERY_PARAMS = new Set([
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref',
]);

/**
 * Strip credentials from a URL path before it reaches GA. On /r/:token the
 * path segment IS the access credential, and the URL fragment carries the
 * E2E decryption key (#k=…) — GA must never see either. The fragment is
 * dropped unconditionally and query params are allowlisted to utm/ref.
 * /received/:id carries its download token in the fragment (#dt=) and a
 * recipient row id in the path; the id is collapsed too.
 */
export function sanitizePathForAnalytics(rawPath) {
  if (!rawPath) return '/';
  const noHash = rawPath.split('#')[0];
  const [pathPart, queryPart] = noHash.split('?');

  let p = pathPart.replace(/^\/r\/.+$/, '/r/:token').replace(/^\/received\/.+$/, '/received/:id');

  if (queryPart) {
    const kept = new URLSearchParams();
    for (const [k, v] of new URLSearchParams(queryPart)) {
      if (SAFE_QUERY_PARAMS.has(k.toLowerCase())) kept.append(k, v);
    }
    const qs = kept.toString();
    if (qs) p += `?${qs}`;
  }
  return p;
}

function sanitizedLocation() {
  return (
    window.location.origin +
    sanitizePathForAnalytics(window.location.pathname + window.location.search)
  );
}

export function initGA() {
  if (_initialized) return true;
  if (!isRealGaId(MEASUREMENT_ID)) {
    if (import.meta.env.DEV) {
      console.info('[analytics] VITE_GA_ID not set — GA disabled');
    }
    return false;
  }

  // Inject gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  // send_page_view: false — we fire page views ourselves on route change so
  // SPA navigation is tracked correctly. page_location is overridden so the
  // config hit's collected URL is sanitized too (never the raw href).
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
    page_location: sanitizedLocation(),
  });

  _initialized = true;
  return true;
}

export function logPageView(path, title) {
  if (!_initialized || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: sanitizePathForAnalytics(path),
    page_title: title || document.title,
    page_location: sanitizedLocation(),
  });
}

export function logEvent(name, params = {}) {
  if (!_initialized || !window.gtag) return;
  window.gtag('event', name, params);
}

export function getMeasurementId() {
  return _initialized ? MEASUREMENT_ID : null;
}
